import { Box, TextField, Button, Paper, CircularProgress, Typography, alpha, keyframes } from '@mui/material';
import { useState, useRef, useEffect } from 'react';
import { SxProps, Theme } from '@mui/material';

export interface Message {
  name: string;
  message: string;
  justification: 'left' | 'right' | 'center';
}

interface ChatPanelProps {
  messages?: Message[];
  onAddMessage?: (message: string) => void;
  isLoading?: boolean;
  hideInput?: boolean;
  sx?: SxProps<Theme>;
}

// Define the pulse animation
const pulse = keyframes`
  0%, 100% { opacity: 0.4; }
  50% { opacity: 1; }
`;

const ChatPanel: React.FC<ChatPanelProps> = ({ 
  messages: externalMessages, 
  onAddMessage,
  isLoading = false, 
  hideInput = false,
  sx = {}
}) => {
  const [internalMessages, setInternalMessages] = useState<Message[]>([
    { 
      name: 'CribCritic', 
      message: 'Welcome! Ask me anything about this property.',
      justification: 'left'
    }
  ]);

  const [inputText, setInputText] = useState('');

  // Use external messages if provided, otherwise use internal state
  const messages = externalMessages || internalMessages;

  const addMessage = (message: string, name: string = 'User') => {
    if (!externalMessages) {
      const newMessage: Message = {
        name,
        message,
        justification: 'right'
      };
      setInternalMessages(prev => [...prev, newMessage]);
    }
  };

  const handleSendMessage = () => {
    if (inputText.trim()) {
      addMessage(inputText);
      onAddMessage?.(inputText);
      setInputText('');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(e.target.value);
  };

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  return (
    <Paper
      sx={{
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        height: '100%', // Ensure full height
        ...sx
      }}
      elevation={12}
    >
      {/* Chat messages area - will expand to fill available space */}
      <Box
        sx={{
          flex: '1 1 auto',
          overflow: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.panel',
          height: 'calc(100% - 60px)' // Subtract approximate input height
        }}
      >
        {messages.map((msg, index) => (
          <Box 
            key={index} 
            sx={{ 
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.justification === 'right' ? 'flex-end' : 
                          msg.justification === 'center' ? 'center' : 'flex-start'
            }}
          >
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ mb: 0.5, ml: msg.justification === 'left' ? 1.5 : 0, mr: msg.justification === 'right' ? 1.5 : 0 }}
            >
              {msg.name}
            </Typography>
            <Box 
              sx={{ 
                maxWidth: '75%',
                padding: '10px 16px',
                borderRadius: msg.justification === 'right' 
                  ? '18px 18px 4px 18px'
                  : '18px 18px 18px 4px',
                backgroundColor: theme => msg.justification === 'right'
                  ? alpha(theme.palette.primary.main, 0.9)
                  : theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : alpha(theme.palette.grey[100], 1),
                color: theme => msg.justification === 'right' 
                  ? theme.palette.primary.contrastText
                  : theme.palette.text.primary,
                boxShadow: theme => `0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`,
                position: 'relative',
                wordWrap: 'break-word',
                whiteSpace: 'pre-wrap',
                textAlign: 'left'
              }}
            >
              {msg.message}
            </Box>
          </Box>
        ))}

        {/* Typing indicator when loading */}
        {isLoading && (
          <Box 
            sx={{ 
              mb: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start'
            }}
          >
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ mb: 0.5, ml: 1.5 }}
            >
              CribCritic
            </Typography>
            <Box 
              sx={{ 
                maxWidth: '75%',
                padding: '10px 16px',
                borderRadius: '18px 18px 18px 4px',
                backgroundColor: theme => theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.8)
                  : alpha(theme.palette.grey[100], 1),
                color: theme => theme.palette.text.primary,
                boxShadow: theme => `0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`,
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                height: '36px'
              }}
            >
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-flex',
                  alignItems: 'center'
                }}
              >
                <Box 
                  component="span" 
                  sx={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: 'text.secondary',
                    mx: '2px',
                    animation: `${pulse} 1.4s infinite ease-in-out`,
                    animationDelay: '0s'
                  }} 
                />
                <Box 
                  component="span" 
                  sx={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: 'text.secondary',
                    mx: '2px',
                    animation: `${pulse} 1.4s infinite ease-in-out`,
                    animationDelay: '0.2s'
                  }} 
                />
                <Box 
                  component="span" 
                  sx={{ 
                    width: '8px', 
                    height: '8px', 
                    borderRadius: '50%', 
                    backgroundColor: 'text.secondary',
                    mx: '2px',
                    animation: `${pulse} 1.4s infinite ease-in-out`,
                    animationDelay: '0.4s'
                  }} 
                />
              </Box>
            </Box>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Input area - fixed at bottom */}
      {!hideInput && (
        <Box sx={{ 
          display: 'flex',
          gap: 1,
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0, // Prevent shrinking
          position: 'sticky',
          bottom: 0,
          zIndex: 2
        }}>
          <TextField
            fullWidth
            placeholder="Type your message here..."
            variant="outlined"
            size="small"
            value={inputText}
            onChange={handleInputChange}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            disabled={isLoading}
          />
          <Button
            variant="contained"
            sx={{ minWidth: 100 }}
            onClick={handleSendMessage}
            disabled={isLoading}
          >
            {isLoading ? (
              <CircularProgress size={20} color="success" />
            ) : (
              'Send'
            )}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default ChatPanel;